import { useCallback, useEffect, useState } from "react";
import ticketingConfigApi from "../../../api/ticketingConfigApi";
import { CategoryMappingFieldResponse } from "../../../types/ticketingConfig.types";
import { getApiErrorMessage } from "./useTicketingConfig";

export interface CategoryMappingFieldPath {
  categoryId: number | undefined;
  subcategoryId?: number;
  tertiaryId?: number;
}

/**
 * Fetches the custom fields configured for a single Category / SubCategory / Tertiary
 * node via GET /ticketing/config/fields. Disabled (no fetch) while nodeId is undefined —
 * e.g. before a node has been selected in the Category Mapping panels.
 */
export const useCategoryMappingFields = (path: CategoryMappingFieldPath, includeInactive = false) => {
  const [fields, setFields] = useState<CategoryMappingFieldResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { categoryId, subcategoryId, tertiaryId } = path;
  const requiresSubcategory = Object.prototype.hasOwnProperty.call(path, "subcategoryId");
  const requiresTertiary = Object.prototype.hasOwnProperty.call(path, "tertiaryId");

  const refetch = useCallback(async () => {
    if (!categoryId || (requiresSubcategory && !subcategoryId) || (requiresTertiary && !tertiaryId)) {
      setFields([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await ticketingConfigApi.getFields(
        { categoryId, ...(requiresSubcategory ? { subcategoryId } : {}), ...(requiresTertiary ? { tertiaryId } : {}) },
        includeInactive
      );
      setFields(data);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [categoryId, subcategoryId, tertiaryId, requiresSubcategory, requiresTertiary, includeInactive]);

  useEffect(() => {
    setFields([]);
    refetch();
  }, [refetch]);

  return { fields, loading, error, refetch };
};

export default useCategoryMappingFields;
